<template>
  <main-layout
    :title="pageTitle"
    :previewUrl="previewUrl"
    :title-actions-below-mobile="true"
  >
    <template v-slot:titleActions>
      <place-google-sync-action
        :place-id="id"
        class="mr-3"
        @synced="loadBusiness"
      />
    </template>
    <template v-slot:body>
      <v-stepper v-model="stepper" class="overflow-visible">
        <v-stepper-header>
          <v-tabs v-model="stepper" class="sub-tabs" show-arrows>
            <v-tab
              v-for="(step, index) in businessFormComponents"
              :key="`tab_${index}`"
              @click="tabClick(index)"
            >
              {{ step.label }}
            </v-tab>
          </v-tabs>
          <v-divider class="ma-0" />
        </v-stepper-header>

        <component
          v-if="businessFormComponents[stepper]"
          :is="businessFormComponents[stepper].component"
          class="mt-md-2 pb-16 mb-2"
          :business-id="id"
          :business="business"
          :place-types="placeTypes"
          :business-status-options="businessStatusOptions"
          :currencies="currencies"
          :tag-fields="placeTagFields"
          :errors-backend="errorsBackend"
          @success="loadBusiness"
        />
      </v-stepper>
    </template>
  </main-layout>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import SubscriptionsMixin from '@/lib/SubscriptionsMixin';
import PlaceGoogleSyncAction from '@/components/PlaceGoogleSyncAction.vue';
import BusinessFormBasic from '@/views/businesses/BusinessFormBasic.vue';
import BusinessFormLocation from '@/views/businesses/BusinessFormLocation.vue';
import BusinessFormPricing from '@/views/businesses/BusinessFormPricing.vue';
import BusinessFormAmenities from '@/views/businesses/BusinessFormAmenities.vue';
import BusinessFormGallery from '@/views/businesses/BusinessFormGallery.vue';
import BusinessFormSocial from '@/views/businesses/BusinessFormSocial.vue';
import BusinessFormOpeningHours from '@/views/businesses/BusinessFormOpeningHours.vue';

export default {
  mixins: [SubscriptionsMixin],
  components: {
    BusinessFormBasic,
    BusinessFormLocation,
    BusinessFormPricing,
    BusinessFormAmenities,
    BusinessFormGallery,
    BusinessFormSocial,
    BusinessFormOpeningHours,
    PlaceGoogleSyncAction,
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    stepSlug: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      stepper: 0,
      placeTypes: [],
      businessStatusOptions: [],
      currencies: [],
      placeTagFields: [],
      business: null,
      loadedStepOptions: {
        info: false,
        location: false,
        pricing: false,
        amenities: false,
      },
    };
  },
  computed: {
    ...mapGetters(['errorsBackend']),
    pageTitle() {
      return this.business ? this.business.name : '';
    },
    previewUrl() {
      if (!this.business || !this.business.id || !this.business.active) {
        return undefined;
      }

      return this.getSingleBusinessUrl(this.business.id);
    },
    businessFormComponents() {
      return [
        {
          component: BusinessFormBasic,
          label: this.$t('Business Form Basic Info'),
          step: 'info',
        },
        {
          component: BusinessFormLocation,
          label: this.$t('Form Location Title'),
          step: 'location',
        },
        {
          component: BusinessFormPricing,
          label: this.$t('Business Form Pricing'),
          step: 'pricing',
        },
        {
          component: BusinessFormAmenities,
          label: this.$t('Amenities'),
          step: 'amenities',
        },
        {
          component: BusinessFormGallery,
          label: this.$t('Form Gallery Title'),
          step: 'gallery',
        },
        {
          component: BusinessFormSocial,
          label: this.$t('Business Form Social'),
          step: 'social',
        },
        {
          component: BusinessFormOpeningHours,
          label: this.$t('Opening Hours'),
          step: 'opening-hours',
        },
      ];
    },
  },
  watch: {
    stepSlug: {
      handler() {
        this.syncStepperFromRoute();
      },
    },
    stepper: {
      immediate: true,
      handler(value) {
        this.loadOptionsForStep(value);
      },
    },
  },
  mounted() {
    this.syncStepperFromRoute();
    this.loadBusiness();
  },
  methods: {
    syncStepperFromRoute() {
      if (!this.stepSlug) {
        return;
      }

      const stepIndex = this.businessFormComponents.findIndex((item) => item.step === this.stepSlug);
      if (stepIndex >= 0) {
        this.stepper = stepIndex;
      }
    },
    loadOptionsForStep(stepIndex) {
      const component = this.businessFormComponents[stepIndex];
      const step = component && component.step ? component.step : null;

      if (!step || this.loadedStepOptions[step]) {
        return Promise.resolve();
      }

      if (step === 'info') {
        return Promise.all([
          this.$store.dispatch('getPlaceTypes'),
          this.$store.dispatch('getPlaceBusinessStatuses'),
        ])
          .then(([placeTypes, businessStatusOptions]) => {
            this.placeTypes = placeTypes || [];
            this.businessStatusOptions = businessStatusOptions || [];
            this.loadedStepOptions.info = true;
          });
      }

      if (step === 'pricing') {
        return this.$store.dispatch('getPlaceCurrencies')
          .then((currencies) => {
            this.currencies = currencies || [];
            this.loadedStepOptions.pricing = true;
          });
      }

      if (step === 'amenities') {
        return this.$store.dispatch('getPlaceTagFields')
          .then((placeTagFields) => {
            this.placeTagFields = placeTagFields || [];
            this.loadedStepOptions.amenities = true;
          });
      }

      return Promise.resolve();
    },
    loadBusiness() {
      this.$store.dispatch('loading', true);
      axios.get(`panel/ads/places/${this.id}`)
        .then(({ data }) => {
          this.business = data;
        })
        .finally(() => {
          this.$store.dispatch('loading', false);
        });
    },
    tabClick(index) {
      this.stepper = index;

      const component = this.businessFormComponents[index];

      if (component && component.step) {
        window.history.replaceState({}, null, `/listing/business/${this.id}/${component.step}`);
      }
    },
  },
};
</script>

<style lang="scss">
.v-stepper.theme--light {
  box-shadow: none;
}

.v-stepper__header {
  box-shadow: none;
}

.sub-tabs.v-tabs > .v-tabs-bar .v-tab {
  text-transform: none;
}

.sub-tabs.v-tabs .v-tabs-bar {
  border-bottom: solid 1px #dddddd !important;
}
</style>
